import React from 'react';

import { List, Tag } from 'antd';

import styled from 'styled-components';

import { showDateNoTime } from '@libs/NwMoment';

import { useUserPermission } from '@contexts/UserContext';

import { getModel, modelFullName } from '@hooks/API/Contact/UseContactsList';

import NwDivider from '@components/Gui/NwDivider';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';
import { ModelCover } from '@components/ModelCover';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const StyledListItem = styled(List.Item)`
    .ant-list-item-meta {
        align-items: flex-start;

        .ant-list-item-meta-avatar {
            padding: 0 1rem 0 1rem;
            min-width: 14rem;
            display: flex;

            .agency-modelcover {
                margin-right: 1rem;
            }

            .agency-modelcover.is-clickable {
                cursor: pointer;
            }

            .agency-type {
                text-align: center;
                text-transform: uppercase;
                font-size: ${(props) => props.theme.FontSizeSm};
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                min-width: 7rem;

                .agency-type-line {
                    margin-bottom: 0.25rem;

                    .ant-tag {
                        font-size: ${(props) => props.theme.FontSizeXSm};
                        padding: 0 0.25rem;
                        line-height: 1.5;
                        margin-right: 0;
                    }
                }
            }
        }

        .ant-list-item-meta-content {
            .ant-list-item-meta-title {
                margin-bottom: 0;

                .agency-title-container {
                    text-transform: capitalize;

                    &.is-clickable {
                        cursor: pointer;
                    }

                    &.agency-dropped {
                        text-decoration: line-through;
                    }
                }
            }

            .ant-list-item-meta-description {
                color: ${(props) => props.theme.TextColor};
                font-size: ${(props) => props.theme.FontSizeSm};

                .agency-description-container {
                    font-size: ${(props) => props.theme.FontSizeSm};
                    padding-right: 2rem;

                    .agency-booker {
                        margin-top: 0.25rem;
                        font-weight: bold;
                    }

                    .agency-dates,
                    .agency-contract {
                        text-transform: uppercase;
                        color: ${(props) => props.theme.TextColorMuted};
                    }

                    .agency-notes {
                        margin-bottom: 0.25rem;
                    }

                    .agency-commission {
                        margin-top: 0.25rem;
                        font-weight: bold;
                    }
                }
            }
        }
    }
`;

const AgencyModelsListItem = ({
    handleCommissions,
    handleEdit,
    handleRemove,
    modelAgency,
    onModelPreview,
    onEventsSearch,
    previewTrigger = 'all',
    readOnly,
    showEventsSearchAction = false,
}) => {
    const accessAccounting = useUserPermission('AccessAccounting');
    const showPreviewOnCover = ['all', 'cover'].includes(previewTrigger);
    const showPreviewOnTitle = ['all', 'title'].includes(previewTrigger);
    const isPlacement = modelAgency.TypeName?.toLowerCase() === 'placement';
    const hasPercentage = modelAgency.Percentage && modelAgency.Percentage > 0;

    const showModelPreview = () => {
        if (onModelPreview) {
            onModelPreview(modelAgency.Model.ID);
        }
    };

    const AgencyModelIcon = ({ type }) => {
        switch (type.toLowerCase()) {
            case 'mother':
                return <NwIcon size="xl" icon={byPrefixAndName.fasl['medal']} />;
            case 'placement':
                return <NwIcon size="xl" icon={byPrefixAndName.fasl['handshake']} />;
            default:
                return <NwIcon size="xl" icon={byPrefixAndName.fasl['globe']} />;
        }
    };

    const modelFromList = getModel(modelAgency.Model.ID);

    const AgencyModelAvatar = () => (
        <>
            <div className={`agency-modelcover${onModelPreview && showPreviewOnCover ? ' is-clickable' : ''}`}>
                <ModelCover
                    model={modelFromList}
                    onClick={onModelPreview && showPreviewOnCover ? showModelPreview : undefined}
                    maxWidth={60}
                />
            </div>
            <div className="agency-type">
                {modelAgency.Dropped && (
                    <span className="agency-type-line">
                        <Tag color={`${StyleVariables.DangerColor}`}>DROPPED</Tag>
                    </span>
                )}
                <span className="agency-type-line">{modelAgency.TypeName.toUpperCase()}</span>
                <span className="agency-type-line">
                    <AgencyModelIcon type={modelAgency.TypeName} />
                </span>
                {modelAgency.Label && (
                    <span className="agency-type-line">
                        <Tag color={`#${modelAgency.Label.Color}`}>{modelAgency.Label.Name}</Tag>
                    </span>
                )}
            </div>
        </>
    );

    const drowDownMenuActions = [];
    if (handleEdit) {
        drowDownMenuActions.push({
            name: '',
            key: 'edit',
            pinned: true,
            icon: byPrefixAndName.fasl['pen'],
            tooltip: 'Edit model agency',
            action: () => handleEdit(modelAgency),
        });
    }
    if (showEventsSearchAction && onEventsSearch) {
        drowDownMenuActions.push({
            name: '',
            key: 'events-search',
            pinned: true,
            icon: byPrefixAndName.fasl['search'],
            tooltip: 'Search ext and move events',
            action: () => onEventsSearch(modelAgency),
        });
    }
    if (handleRemove) {
        drowDownMenuActions.push({
            name: 'delete',
            type: 'delete',
            key: 'delete',
            icon: byPrefixAndName.fasl['trash-alt'],
            confirmationPlacement: 'left',
            action: () => handleRemove(modelAgency),
        });
    }
    if (handleCommissions && (hasPercentage || isPlacement)) {
        const actionName = isPlacement ? 'show commissions due' : 'show moth. ag. comm.';
        drowDownMenuActions.push({
            name: actionName,
            key: 'commissions',
            icon: byPrefixAndName.fasl['percent'],
            action: () => handleCommissions(modelAgency),
        });
    }

    const AgencyModelDescription = () => {
        return (
            <div className={`agency-description-container${modelAgency.Dropped ? ' agency-dropped' : ''}`}>
                {modelAgency.Notes && (
                    <>
                        <NwDivider lessMargin>Booking Notes</NwDivider>
                        <div className="agency-notes">{modelAgency.Notes}</div>
                    </>
                )}
                {accessAccounting && modelAgency.AccountingNotes && (
                    <>
                        <NwDivider lessMargin>Accounting Notes</NwDivider>
                        <div className="agency-notes-accounting-notes">{modelAgency.AccountingNotes}</div>
                    </>
                )}
                {modelAgency.Towns && <div className="agency-dates">Towns: {modelAgency.Towns}</div>}
                {modelAgency.Booker && <div className="agency-booker">Booker: {modelAgency.Booker}</div>}
                {modelAgency.ContractDate && (
                    <div className="agency-contract">Contract signed on {showDateNoTime(modelAgency.ContractDate)}</div>
                )}
                {!modelAgency.DateIn && modelAgency.DateOut && (
                    <div className="agency-dates">left on {showDateNoTime(modelAgency.DateOut)}</div>
                )}
                {modelAgency.DateIn && !modelAgency.DateOut && (
                    <div className="agency-dates">since {showDateNoTime(modelAgency.DateIn)}</div>
                )}
                {modelAgency.DateIn && modelAgency.DateOut && (
                    <div className="agency-dates">{`${showDateNoTime(modelAgency.DateIn)} > ${showDateNoTime(modelAgency.DateOut)}`}</div>
                )}
                {modelAgency.Percentage > 0 && (
                    <div className="agency-commission">{`${modelAgency.Percentage}% comm. ${modelAgency.OnGross ? ' on model gross' : ' on model net'}`}</div>
                )}
            </div>
        );
    };

    const AgencyModelTitle = () => (
        <span
            onClick={onModelPreview && showPreviewOnTitle ? showModelPreview : undefined}
            className={`agency-title-container${onModelPreview && showPreviewOnTitle ? ' is-clickable' : ''}${
                modelAgency.Dropped ? ' agency-dropped' : ''
            }`}
        >
            <strong>{modelFullName(modelAgency.Model)}</strong>
        </span>
    );

    return (
        <StyledListItem
            actions={
                !readOnly && drowDownMenuActions.length > 0 ? [<NwDropDownMenu actions={drowDownMenuActions} />] : []
            }
        >
            <List.Item.Meta
                avatar={<AgencyModelAvatar />}
                title={<AgencyModelTitle />}
                description={<AgencyModelDescription />}
            />
        </StyledListItem>
    );
};

export default AgencyModelsListItem;
